import React, { useState } from 'react';
import { useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { LoginForm } from '@/components/LoginForm';
import { RegisterForm } from '@/components/RegisterForm';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useNoIndexPage } from '@/hooks/useSEO';

type AuthMode = 'login' | 'register';

export function Login() {
  const [, setLocation] = useLocation();
  const [authMode, setAuthMode] = useState<AuthMode>('login');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);

  useNoIndexPage(
    authMode === 'register' ? 'Registrarse | Statemia' : 'Iniciar sesión | Statemia',
    'Accede a tu cuenta de Statemia.'
  );

  // Check for verification success in URL
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const verified = urlParams.get('verified');
    const message = urlParams.get('message');
    
    if (verified === 'true' && message) {
      setShowVerificationMessage(true);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

      const handleGoogleLogin = () => {
        // Google OAuth will redirect to home page after successful login
        window.location.href = '/api/auth/signin/google';
      };

  const handleBackToHome = () => {
    setLocation('/');
  };

  const handleRegisterSuccess = () => {
    setShowSuccessMessage(true);
    setTimeout(() => {
      setAuthMode('login');
      setShowSuccessMessage(false);
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-4">
        {/* Success message for registration */}
        {showSuccessMessage && (
          <Alert className="border-green-200 bg-green-50 text-green-800">
            <AlertDescription>
              ¡Registro exitoso! Revisa tu email para verificar tu cuenta.
            </AlertDescription>
          </Alert>
        )}

        {/* Success message for email verification */}
        {showVerificationMessage && (
          <Alert className="border-green-200 bg-green-50 text-green-800">
            <AlertDescription>
              ¡Email verificado exitosamente! Ya puedes iniciar sesión.
            </AlertDescription>
          </Alert>
        )}

        {/* Email/Password Forms */}
        {authMode === 'login' ? (
          <LoginForm 
            onSuccess={() => window.location.href = '/'}
            onSwitchToRegister={() => setAuthMode('register')}
          />
        ) : (
          <RegisterForm 
            onSuccess={handleRegisterSuccess}
            onSwitchToLogin={() => setAuthMode('login')}
          />
        )}

        {/* OAuth Section */}
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    Or continue with
                  </span>
                </div>
              </div>

              {/* Google Login */}
              <Button 
                onClick={handleGoogleLogin}
                className="w-full px-6 py-3 rounded-lg flex items-center justify-center gap-3 bg-white hover:bg-gray-50 text-gray-900 border border-gray-300"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Back to Home */}
        <div className="text-center">
          <Button 
            variant="ghost" 
            onClick={handleBackToHome}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            ← Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
}